import {Component, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ApiService, HttpError} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {ModalComponent} from '../../shared/modal/modal.component';
import {NavigationService} from '../../shared/navigation.service';
import {ProfileAvatarImageComponent} from '../profile-avatar-image/profile-avatar-image.component';

@Component({
  selector: 'profile-form',
  templateUrl: './profile-form.component.html',
  styleUrls: ['../styles/account-form.scss', './profile-form.component.scss']
})
export class ProfileFormComponent implements OnInit {

  @ViewChild('successModal') successModal: ModalComponent;
  @ViewChild('avatarModal') avatarModal: ModalComponent;
  @ViewChild('profileAvatarImage') profileAvatarImage: ProfileAvatarImageComponent;

  profileInformation: FormGroup;
  countries: Map<string, string> = new Map();
  isLoading = false;
  success = false;
  user = this.Auth.user$;
  isPhotoEnabled = environment.flags.profilePhoto;
  masquerading$ = this.Auth.masquerading$;

  constructor(private formBuilder: FormBuilder,
              private Api: ApiService,
              private Navigation: NavigationService,
              private Auth: AuthService) {
  }

  get namePart() {
    return this.profileInformation.get('name');
  }

  get firstName() {
    return this.profileInformation.get('name.firstName');
  }

  get lastName() {
    return this.profileInformation.get('name.lastName');
  }

  get avatar() {
    return this.profileInformation.get('name.avatar');
  }

  get addressPart() {
    return this.profileInformation.get('address');
  }

  get street() {
    return this.profileInformation.get('address.street');
  }

  get phonePart() {
    return this.profileInformation.get('phone');
  }

  isFirstNameInvalid() {
    return this.firstName.invalid && !this.isLoading;
  }

  isLastNameInvalid() {
    return this.lastName.invalid && !this.isLoading;
  }

  isStreetAddressInvalid() {
    return this.street.invalid && !this.isLoading;
  }

  ngOnInit() {
    this.initializeForm();
    this.loadData();
  }

  initializeForm() {
    this.profileInformation = this.formBuilder.group({
      name: this.initializeName(),
      address: this.initializeAddress(),
      phone: this.initializePhone()
    });
  }

  initializeName() {
    return this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      friendlyName: [''],
      title: [''],
      avatar: ['']
    });
  }

  initializeAddress() {
    return this.formBuilder.group({
      street: [''],
      city: [''],
      postalCode: [''],
      state: [''],
      country: ['']
    });
  }

  initializePhone() {
    return this.formBuilder.group({
      main: [''],
      current: ['']
    });
  }

  loadData() {
    this.isLoading = true;

    forkJoin(
      this.Api.getCountriesList(),
      this.Api.getUserProfileInfo()
    ).subscribe(([countryResponse, profileResponse]) => {
      this.updateForm(countryResponse, profileResponse);
      this.isLoading = false;
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  reloadData() {
    this.Api.getUserProfileInfo().subscribe(profileResponse => {
      this.updateProfileInformation(profileResponse);
      this.updateUser();
      this.isLoading = false;
      this.showSuccessModal();
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  showSuccessModal() {
    this.successModal.show();
    window.setTimeout(() => this.successModal.hide(), 4000);
  }

  hideSuccessModal() {
    this.successModal.hide();
  }

  updateForm(countryResponse: CountryListDropdownResponse, profileResponse: PersonProfileInformation) {
    this.updateCountries(countryResponse);
    this.updateProfileInformation(profileResponse);
  }

  updateCountries(countryResponse: CountryListDropdownResponse) {
    this.countries = new Map();

    for (const country of countryResponse.countries) {
      this.countries.set(country.isoCode, country.countryName);
    }
  }

  updateProfileInformation(profileResponse: PersonProfileInformation) {
    trimState(profileResponse);
    this.profileInformation.patchValue(profileResponse, {onlySelf: true});
    this.profileInformation.markAsPristine();
    this.profileInformation.markAsUntouched();
  }

  updateUser() {
    const firstName = this.namePart.value.firstName;
    const lastName = this.namePart.value.lastName;
    const friendlyName = this.namePart.value.friendlyName;
    const user = {...this.Auth.user$.getValue()};

    user.friendlyName = friendlyName;
    user.firstName = firstName;
    user.lastName = lastName;

    this.Auth.user$.next(user);
  }

  update() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.hideSuccessModal();
    this.isLoading = true;

    const alteredModel = this.removeUnchangedSectionsFromModel();

    this.Api.updateProfileInformation(alteredModel).subscribe(() => {
      this.reloadData();
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  removeUnchangedSectionsFromModel(): PersonProfileInformation {
    return {
      name: this.namePart.dirty ? this.namePart.value : undefined,
      address: this.addressPart.dirty ? this.addressPart.value : undefined,
      phone: this.phonePart.dirty ? this.phonePart.value : undefined
    };
  }

  handleError(err: HttpError) {
    console.error('Failed to save or load profile information', err);
    this.isLoading = false;
    this.Navigation.handleError(err);
  }

  isUpdateButtonDisabled() {
    return this.profileInformation.pristine || this.profileInformation.invalid;
  }

  fileChanged(event: Event){
    let file: File = (<HTMLInputElement> event.srcElement).files[0];
    this.avatarModal.show();
    this.profileAvatarImage.readFile(file);
  }

  onNewImage(src) {
    this.Api.updateProfileAvatar(src).subscribe(() => {
      this.updateUserAvatar();
    }, (err: HttpError) => {
      this.handleError(err);
    });

    this.profileAvatarImage.reset();
  }

  hasAvatar() {
    const user = {...this.Auth.user$.getValue() };
    return (user.avatarUrl);
  }

  updateAvatar() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.profileAvatarImage.getImage();
    this.avatarModal.hide();
  }

  clearAvatar(deep: boolean) {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.avatarModal.hide();
    if (deep) {
      this.Api.deleteProfileAvatar().subscribe(() => {
        this.updateUserAvatar();
      }, (err: HttpError) => {
        this.handleError(err);
      });
    }
  }

  updateUserAvatar() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.Api.getProfileAvatar().subscribe((url) => {
      const user = {...this.Auth.user$.getValue() };
      user.avatarUrl = url.avatarUrl;
      this.Auth.user$.next(user);
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

}

function trimState(profileResponse: PersonProfileInformation) {
  const state = profileResponse.address.state;

  if (state) {
    profileResponse.address.state = state.trim();
  }
}
